import store from '@/store'
import moment from 'moment'
import useDiscountCard from '@/composables/discountCards'
import router from '@/router'
import { computed } from 'vue'
import { getSystemModules } from '@/composables/currentUser'
import {
  createDentistInsertForm1M, createMedicalInterventionPreliminaryConsent, createMedicalInterventionVoluntaryConsent,
  createOrthodonticInsert, createOrthopedicInsert, createQuestionnaireForm1K, createTreatmentDiaryDocument
} from '@/composables/pdfHelper'

export default function usePatient() {
  const { discountCards } = useDiscountCard()
  const openedCardPatientID = computed(
    () => router.currentRoute.params.patientId ? +router.currentRoute.params.patientId : null
  )
  const openedCardPatient = computed(() => store.getters['patient/getPatient'])
  const patientsForDropdown = computed(() => store.getters['patient/patientsForDropdown'])
  const userCanGoToPatientList = getSystemModules().includes('patient-cards')

  const getPatientDiscountCardByCardId = discountCardId => discountCards.value.find(dc => dc.id === discountCardId)
  const loadPatientById = async patientId => await store.dispatch('patient/getPatient', patientId)
  const loadPatientPaymentsInfoById = async patientId =>
    await store.dispatch('patient/getPatientPaymentsInfo', patientId)
  const fetchPatientsForDropdown = async (ignoreCache = false, params = {}) => {
    if (ignoreCache || !patientsForDropdown.value.length) {
      await store.dispatch('patient/getPatientsList', params)
    }
  }
  const getPatientDocumentInfo = patient => {
    const docTypes = store.getters['patient/getDocumentTypes']
    if (patient.document_type && patient.document_type === 'passport' && patient.document_identifier) {
      const doc = docTypes.find(dt => dt.value === patient.document_type)
      if (doc) {
        const info = [doc.text, patient.document_identifier]
        if (patient.document_issue_date || patient.document_issued_by) {
          info.push('Выдан')
          if (patient.document_issue_date) {
            info.push(moment(patient.document_issue_date).format('DD.MM.YYYY'))
          }
          if (patient.document_issued_by) {
            info.push(patient.document_issued_by)
          }
        }
        return info
      }
    }
    return null
  }
  const generateLastVisiting = async () => {
    const patientId = parseInt(router.currentRoute.params.patientId)
    if (patientId) {
      await store.dispatch('patient/getPatient', patientId)
      const patient = store.getters['patient/getPatient']
      const treatmentDiaries = store.getters['patient/getPatientTreatmentDiaries']
      await createTreatmentDiaryDocument(
        treatmentDiaries[treatmentDiaries.length - 1], patient, 'Последнее посещение', true
      )
    }
  }
  const generateDentistInsertForm1M = async () => {
    const patientId = parseInt(router.currentRoute.params.patientId)
    if (patientId) {
      await store.dispatch('patient/getPatient', patientId)
      await store.dispatch('patient/getPatientDentalCardInsertForm1M', patientId)
      await store.dispatch('patient/fetchPatientDentalCardInsertForm1MResult', patientId)
      const patient = store.getters['patient/getPatient']
      const form1M = store.getters['patient/dentalCardInsertForm1M']
      const form1MResults = store.getters['patient/dentalCardInsertForm1MResults']
      if (patient) {
        createDentistInsertForm1M(patient.full_name, form1M, form1MResults)
      }
    }
  }
  const generateQuestionnaireForm1K = async () => {
    const patientId = parseInt(router.currentRoute.params.patientId)
    if (patientId) {
      await store.dispatch('patient/getPatient', patientId)
      await store.dispatch('patient/getPatientDentalCardInsertForm1K', patientId)
      const patient = store.getters['patient/getPatient']
      const form1K = store.getters['patient/dentalCardInsertForm1K']
      if (patient) {
        createQuestionnaireForm1K(patient.full_name, form1K)
      }
    }
  }
  const generateMedicalInterventionVoluntaryConsent = async () => {
    const patientId = parseInt(router.currentRoute.params.patientId)
    if (patientId) {
      await store.dispatch('patient/getPatient', patientId)
      let payload = { patientId: patientId, type: 'DENTAL' }
      await store.dispatch('patient/getPatientTreatmentDiaries', payload)
      const patient = store.getters['patient/getPatient']
      const treatmentDiaries = store.getters['patient/getPatientTreatmentDiaries']
      if (patient) {
        createMedicalInterventionVoluntaryConsent(patient.full_name, patient.last_name, treatmentDiaries)
      }
    }
  }
  const generateMedicalInterventionPreliminaryConsent = async () => {
    const patientId = parseInt(router.currentRoute.params.patientId)
    if (patientId) {
      await store.dispatch('patient/getPatient', patientId)
      await store.dispatch('patient/getPatientVisualInspection', patientId)
      const patient = store.getters['patient/getPatient']
      const visualInspection = store.state.patient.visualInspection
      const dentalCardDate = visualInspection && visualInspection.date
        ? moment(parseInt(visualInspection.date)).format('LL') : moment().format('LL')
      if (patient) {
        createMedicalInterventionPreliminaryConsent(patient.full_name, dentalCardDate)
      }
    }
  }
  const generateOrthodonticInsert = async () => {
    const patientId = parseInt(router.currentRoute.params.patientId)
    if (patientId) {
      await store.dispatch('patient/getPatient', patientId)
      await store.dispatch('patient/getPatientDentalCardInsertFormOrthodontic', patientId)
      const patient = store.getters['patient/getPatient']
      const orthodonticForm = store.getters['patient/dentalCardInsertFormOrthodontic']
      if (patient) {
        createOrthodonticInsert(patient.full_name, orthodonticForm)
      }
    }
  }
  const generateOrthopedicInsert = async () => {
    const patientId = parseInt(router.currentRoute.params.patientId)
    if (patientId) {
      await store.dispatch('patient/getPatient', patientId)
      await store.dispatch('patient/getPatientDentalCardInsertFormOrthopedic', patientId)
      const patient = store.getters['patient/getPatient']
      const orthopedicForm = store.getters['patient/dentalCardInsertFormOrthopedic']
      if (patient) {
        createOrthopedicInsert(patient.full_name, orthopedicForm)
      }
    }
  }
  const getDocumentTypeName = docType => {
    const docTypes = store.getters['patient/getDocumentTypes']
    const doc = docTypes.find(dt => dt.value === docType)
    return doc ? doc.text : ''
  }

  return {
    openedCardPatientID,
    openedCardPatient,
    patientsForDropdown,
    userCanGoToPatientList,

    getPatientDiscountCardByCardId,
    loadPatientById,
    loadPatientPaymentsInfoById,
    fetchPatientsForDropdown,
    getPatientDocumentInfo,
    generateLastVisiting,
    generateDentistInsertForm1M,
    generateQuestionnaireForm1K,
    generateMedicalInterventionVoluntaryConsent,
    generateMedicalInterventionPreliminaryConsent,
    generateOrthodonticInsert,
    generateOrthopedicInsert,
    getDocumentTypeName,
  }
}
